// Builds the `common` CV variant as a docx Document, mirroring CommonPdf.jsx
// (section order: header, introduction, languages, business skills, experiences,
// diplomas, hobbies, footer).

import { Document, LineRuleType } from "docx";
import { extractResumeDataDocumentSection } from "../../../hooks/useResumeDocumentSection";
import { extractResumeProperties } from "../../../hooks/useResumeProperties";
import { loadImage } from "../shared/wordImage";
import { pageNumberFooter, para, sectionTitle, separateTables } from "../shared/wordHelpers";
import { commonAccent, FONT_ARIAL } from "../shared/wordTheme";
import { A4, ptToHalfPoints, pxToTwips } from "../shared/wordUnits";
import { buildCommonHeader } from "./sections/commonHeader";
import { buildCommonIntroduction } from "./sections/commonIntroduction";
import { buildCommonLanguages } from "./sections/commonLanguages";
import { buildCommonBusinessSkills } from "./sections/commonBusinessSkills";
import { buildCommonExperiences } from "./sections/commonExperiences";
import { buildCommonDiplomas } from "./sections/commonDiplomas";
import { buildCommonHobbies } from "./sections/commonHobbies";

export async function buildCommonWord( resumeData, translations ) {
    const data = extractResumeDataDocumentSection( resumeData );
    const baseProps = extractResumeProperties( resumeData );
    const isLusis = ( baseProps.logoLabel || "" ).toLowerCase().includes( "lusis" )
        || ( baseProps.logoSrc || "" ).toLowerCase().includes( "lusis" );
    const props = { ...baseProps, isLusis };
    const accent = commonAccent( props.isAlcyone );

    const [ logoImage, spinnerImage ] = await Promise.all( [
        loadImage( props.logoSrc ),
        props.isAlcyone ? loadImage( "/spinner-alcyone.png" ) : Promise.resolve( null ),
    ] );

    const ctx = { data, props, translations, accent, font: FONT_ARIAL, logoImage, spinnerImage };
    const t = translations?.pdf ?? {};
    const spacer = () => para( [], { spacing: { before: pxToTwips( 10 ), after: 0 } } );

    const children = [];
    children.push( ...buildCommonIntroduction( ctx ) );

    children.push( spacer(), sectionTitle( t.languageSkillsTitle, accent, { font: FONT_ARIAL } ) );
    children.push( buildCommonLanguages( ctx ) );

    children.push( spacer(), sectionTitle( t.businessSkillsTitle, accent, { font: FONT_ARIAL } ) );
    children.push( ...buildCommonBusinessSkills( ctx ) );

    if ( !props.isExperienceHidden ) {
        children.push( spacer(), sectionTitle( t.experiencesTitle, accent, { font: FONT_ARIAL } ) );
        children.push( ...buildCommonExperiences( ctx ) );
    }

    children.push( spacer(), sectionTitle( t.diplomasTitle, accent, { font: FONT_ARIAL } ) );
    children.push( ...buildCommonDiplomas( ctx ) );

    children.push( spacer(), sectionTitle( t.hobbiesTitle, accent, { font: FONT_ARIAL } ) );
    children.push( ...buildCommonHobbies( ctx ) );

    return new Document( {
        styles: {
            default: {
                document: {
                    run: { font: FONT_ARIAL, size: ptToHalfPoints( 10 ) },
                    paragraph: { spacing: { after: 0, line: 240, lineRule: LineRuleType.AUTO } },
                },
            },
        },
        sections: [
            {
                properties: {
                    page: {
                        size: { width: A4.width, height: A4.height },
                        margin: {
                            top: pxToTwips( 110 ),
                            right: pxToTwips( 40 ),
                            bottom: pxToTwips( 80 ),
                            left: pxToTwips( 40 ),
                            header: pxToTwips( props.isAlcyone ? 20 : 60 ),
                            footer: pxToTwips( 20 ),
                        },
                    },
                },
                headers: { default: buildCommonHeader( ctx ) },
                footers: { default: pageNumberFooter( { font: FONT_ARIAL } ) },
                children: separateTables( children ),
            },
        ],
    } );
}
